<template>
  <md-card
    class="dosage-count-check"
    :class="{disabled: disabled}"
    :title="titleText"
  >
    <md-card-content class="dosage-count-content">
      <div class="options-panel-switcher">
        <el-form class="form">
          <el-form-item class="form-item">
            <el-switch
              v-if="dosageAvailable && countAvailable"
              :value="state.activeOptionsPanel"
              on-text="with dosage"
              off-text="with count"
              on-color="#3fabd9"
              off-color="#62cd63"
              on-value="dosage"
              off-value="count"
              :width="110"
              :disabled="disabled || !dosageAvailable || !countAvailable"
              @input="switchActiveOptionsPanel"
            />
          </el-form-item>
        </el-form>
      </div>
      <div v-if="(mode === 'DOSAGE' || mode === 'BOTH') && state.activeOptionsPanel === 'dosage'" class="dosage-options">
        <el-input
          type="number"
          :disabled="disabled || !dosageAvailable"
          :value="dosage ? dosage.min : null"
          placeholder="any"
          class="dosage-input"
          @change="min => updateDosage({min: parseFloat(min)})"
        >
          <template slot="prepend">{{ prefix || 'between' }}</template>
        </el-input>
        <el-input
          type="number"
          :disabled="disabled || !dosageAvailable"
          :value="dosage ? dosage.max : null"
          placeholder="any"
          class="dosage-input"
          @change="max => updateDosage({max: parseFloat(max)})"
        >
          <template slot="prepend">and</template>
          <template v-if="suffix" slot="append">{{ suffix }}</template>
        </el-input>

        <el-select
          v-if="unitsAvailable"
          id="units-select"
          v-close-on-zoom
          :value="units"
          :placeholder="unitPlaceholder || 'units'"
          class="units-select"
          :disabled="disabled || !dosageAvailable"
          @input="value => updateUnits(value)"
        >
          <el-option
            v-for="unit in unitsAvailable"
            :key="unit.value"
            :label="unit.label"
            :value="unit.value"
          />
          <el-option
            v-for="unit in unavailableSelectedUnits"
            :key="unit.value"
            :label="unit.label"
            :value="unit.value"
            :disabled="true"
          />
        </el-select>
      </div>
      <div v-if="(mode === 'COUNT' || mode === 'BOTH') && state.activeOptionsPanel === 'count'" class="count-options">
        <case-logic-label v-if="prefix">
          {{ prefix }}
        </case-logic-label>
        <el-select
          :value="countOperator"
          placeholder="="
          class="count-operator-select"
          width="20px"
          :disabled="disabled || !countAvailable"
          @input="operator => updateCountOperator(operator)"
        >
          <el-option v-for="op in operators" :key="op.value" :label="op.label" :value="op.value" />
        </el-select>
        <el-input
          type="number"
          :disabled="disabled || !countAvailable"
          :value="count"
          :min="0"
          placeholder="any"
          class="count-input"
          @change="count => updateCount(parseFloat(count))"
        />
        <help-tooltip v-if="countWarning" type="warning">
          {{ countWarning.message }}
        </help-tooltip>
      </div>
    </md-card-content>
  </md-card>
</template>

<script>
import isNumber from 'lodash/isNumber';
import HelpTooltip from '~lib/common/components/HelpTooltip';
import { unitTypes } from '~lib/common/config/medication';
import CaseLogicLabel from './CaseLogicLabel';

export default {
  name: 'dosage-count-check',
  components: {
    CaseLogicLabel,
    HelpTooltip,
  },
  props: {
    mode: {
      type: String,
      validator: mode => ['COUNT', 'DOSAGE', 'BOTH'].includes(mode),
      default: 'BOTH',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    dosageAvailable: {
      type: Boolean,
      default: true,
    },
    countAvailable: {
      type: Boolean,
      default: true,
    },
    dosage: {
      type: Object,
      default: null,
    },
    count: {
      type: Number,
      default: null,
    },
    countOperator: {
      type: String,
      default: null,
    },
    units: {
      type: String,
      default: null,
    },
    unitsAvailable: {
      type: Array,
      default: () => null,
    },
    unitPlaceholder: {
      type: String,
      default: null,
    },
    countWarning: {
      type: Object,
      default: null,
    },
    prefix: {
      type: String,
      default: null,
    },
    suffix: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      state: {
        activeOptionsPanel: this.mode === 'COUNT' ? 'count' : 'dosage',
      },
      operators: [
        { value: 'LESS_THAN', label: '<' },
        { value: 'LESS_THAN_OR_EQUAL', label: '≤' },
        { value: 'EQUAL', label: '=' },
        { value: 'NOT_EQUAL', label: '≠' },
        { value: 'GREATER_THAN_OR_EQUAL', label: '≥' },
        { value: 'GREATER_THAN', label: '>' },
      ],
    };
  },
  computed: {
    titleText() {
      if (this.state.activeOptionsPanel === 'dosage' && !this.dosageAvailable) {
        return 'You can only check the dosage on 1 route at a time';
      } else if (this.state.activeOptionsPanel === 'count' && !this.countAvailable) {
        return 'You can only check the count on 1 specific route or any route';
      }
      return '';
    },
    unavailableSelectedUnits() {
      return unitTypes.filter(unit => !(this.unitsAvailable || []).includes(unit) && this.units === unit.value);
    }
  },
  watch: {
    mode() {
      this.state.activeOptionsPanel = this.mode === 'COUNT' || isNumber(this.count)
        ? 'count'
        : 'dosage';
    }
  },
  mounted() {
    this.state.activeOptionsPanel = this.mode === 'COUNT' || isNumber(this.count)
      ? 'count'
      : 'dosage';
  },
  methods: {
    switchActiveOptionsPanel(activeOptionsPanel) {
      if (this.mode !== 'BOTH') {
        return;
      }
      this.state.activeOptionsPanel = activeOptionsPanel;
      if (activeOptionsPanel === 'dosage') {
        this.updateCount(null);
        this.updateUnits(null);
      } else if (activeOptionsPanel === 'count') {
        this.resetDosage();
      }
    },
    updateDosage(updates) {
      this.$emit('updateDosage', {
        ...this.dosage,
        ...updates
      });
    },
    updateCountOperator(operator) {
      this.$emit('updateCountOperator', operator);
    },
    updateCount(count) {
      this.$emit('updateCount', count !== null ? Math.max(count, 0) : null);
    },
    updateUnits(units) {
      this.$emit('updateUnits', units);
    },
    resetDosage() {
      this.updateDosage({ min: null, max: null });
    },
  },
};
</script>

<style scoped>
.dosage-count-check.disabled {
  background-color: rgb(238, 239, 246);
  cursor: not-allowed;
}

.dosage-count-check >>> .el-switch.is-disabled .el-switch__core {
  background-color: darkgray !important;
}

.dosage-count-content {
  padding: 5px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}

.options-panel-switcher {
  padding: 0 5px;
}

.dosage-options,
.count-options {
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
}

.count-options {
  justify-content: flex-start;
}

.dosage-input, .count-input {
  width: auto;
}

.dosage-input >>> input {
  width: 110px;
}

.count-input >>> input {
  width: 110px;
}

.count-operator-select {
  width: 150px;
}

.el-form-item {
  margin: 0;
}

.help-tooltip {
  margin: 0 4px;
}
</style>